/**
 * Directory search box. Type two letters, pick a person. Used for the assignee.
 */

import { useState } from 'react';
import type { SdPerson } from '@ampersandbrands/ui';
import { usePeopleSearch } from '@/lib/useServiceDesk';
import { SD_INPUT, SD_MUTED } from './sdStyles';

function PersonResults({
  people,
  loading,
  onPick,
}: {
  people: SdPerson[];
  loading: boolean;
  onPick: (p: SdPerson) => void;
}) {
  if (loading && people.length === 0) return <p className={`px-2 py-1.5 text-[12px] ${SD_MUTED}`}>Searching...</p>;
  if (people.length === 0) return <p className={`px-2 py-1.5 text-[12px] ${SD_MUTED}`}>No one found.</p>;
  return (
    <ul className="max-h-56 overflow-y-auto">
      {people.slice(0, 12).map((p) => (
        <li key={p.id}>
          <button
            type="button"
            onClick={() => onPick(p)}
            className="flex w-full flex-col px-2 py-1.5 text-left text-[13px] hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm"
          >
            <span className="text-amp-ink dark:text-amp-dark-ink">{p.name}</span>
            <span className={`text-[12px] ${SD_MUTED}`}>{[p.title, p.email].filter(Boolean).join(' - ')}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function PersonPicker({
  onPick,
  placeholder = 'Search the directory',
}: {
  onPick: (p: SdPerson) => void;
  placeholder?: string;
}) {
  const [q, setQ] = useState('');
  const search = usePeopleSearch(q);
  return (
    <div className="relative">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={SD_INPUT}
      />
      {q.trim().length >= 2 && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-amp-line bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <PersonResults
            people={search.data ?? []}
            loading={search.isFetching}
            onPick={(p) => {
              setQ('');
              onPick(p);
            }}
          />
        </div>
      )}
    </div>
  );
}
